/**
 * Money, for the SPA (CLAUDE.md non-negotiables, spec §11.2, §13).
 *
 * THIS MODULE ADDS NO ARITHMETIC AND NO FORMATTING. It re-exports
 * `@steelbill/tax` and stops. Its whole purpose is to be the import that a
 * screen author reaches for, so that `formatPaise` is the obvious thing to
 * find and `toLocaleString` never is.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * NEVER `Number(paise / 100).toLocaleString("en-IN")`.
 *
 * CLAUDE.md: "Format only through formatPaise / formatRupees in packages/tax;
 * never call toLocaleString('en-IN') directly." The reason is in
 * packages/tax/src/format-money.ts: `Intl` grouping depends on the ICU data
 * compiled into the runtime, and a runtime built with `small-icu` resolves
 * `en-IN` to plain `en` and emits WESTERN grouping — `₹145,600.00` — with no
 * error and no warning. The owner reads that as a bug in the software, so it is
 * a correctness issue, not a cosmetic one (§11.2: "Indian digit grouping on
 * every amount").
 *
 * The division is the second reason: money is integer paise everywhere (§4),
 * and `paise / 100` is the exact moment a rupee amount becomes a float.
 * ─────────────────────────────────────────────────────────────────────────────
 */

export {
  RUPEE_SIGN,
  formatPaise,
  formatRupees,
  groupIndianDigits,
  type MoneyFormatOptions,
} from "@steelbill/tax";

export { formatInvoiceNumber, formatQuoteNumber } from "@steelbill/tax";

import { formatPaise } from "@steelbill/tax";

/**
 * Compact rupees for a tight chip or a list row: `₹1,45,600` — no paise.
 *
 * DISPLAY ONLY, and never for a document total. `formatPaise({ decimals: 0 })`
 * truncates toward zero, so `₹12,980.60` shows as `₹12,980`. That is fine for
 * "This month: 12 bills, ₹1,45,600" (§11.2) where the figure is a sense of
 * scale, and wrong for anything the customer will be asked to pay. Use
 * {@link formatRupees} there.
 */
export function formatRupeesCompact(paise: number): string {
  return formatPaise(paise, { symbol: true, decimals: 0 });
}

/**
 * Parse a rupee amount the owner typed into integer paise.
 *
 * Every rate and amount field in the app hands its string here rather than to
 * `parseFloat`. `Math.round(parseFloat("67.50") * 100)` is 6750 today and a
 * floating-point surprise on some other value; splitting the string on the
 * decimal point and reading the two halves as integers cannot drift.
 *
 * Accepts what a human types: `1,45,600`, `1,45,600.50`, `₹6750`, ` 67.5 `.
 * Returns `null` for anything it cannot read — never `NaN`, never `0`, because
 * a silent zero on a rate field means an invoice for nothing.
 */
export function parseRupeesToPaise(input: string): number | null {
  const cleaned = input.replace(/[₹\s,]/g, "");
  if (cleaned === "") return null;

  const match = /^(-?)(\d*)(?:\.(\d{0,2}))?$/.exec(cleaned);
  if (match === null) return null;

  const [, sign = "", whole = "", fraction] = match;
  if (whole === "" && (fraction === undefined || fraction === "")) return null;

  const rupees = whole === "" ? 0 : Number.parseInt(whole, 10);
  const paise = fraction === undefined || fraction === "" ? 0 : Number.parseInt(fraction.padEnd(2, "0"), 10);

  if (!Number.isSafeInteger(rupees) || !Number.isSafeInteger(paise)) return null;

  const total = rupees * 100 + paise;
  return sign === "-" ? -total : total;
}

/**
 * Integer paise → the editable string for an input: `6750` → `"67.50"`.
 *
 * No grouping separators: a comma inside a field the owner is mid-way through
 * editing moves the caret and is the classic reason a rate gets typed twice.
 * Grouping is for DISPLAY (`formatRupees`), not for input.
 */
export function paiseToInputValue(paise: number): string {
  const negative = paise < 0;
  const absolute = Math.abs(paise);
  const whole = Math.floor(absolute / 100);
  const fraction = absolute % 100;
  return `${negative ? "-" : ""}${whole}.${String(fraction).padStart(2, "0")}`;
}
